// EmrConnecting.js — stands in for the note while the visit's note type is
// read from the EMR: the Comprehend mark and the clinic's EMR logo with dots
// pulsing between them (the same language as the partner-app splash), in a
// dim panel about the height of a four-section note, so nothing half-loaded
// shows and the page doesn't jump much when the note appears.
import { EMRS } from "../../constants/EmrNoteTypeMapping";
import logo from "../../assets/img/brand/logo.png";

const STYLE_ID = "cpt-emr-connecting-style";

// About the note type row, the Scan / Learn row and four sections.
const NOTE_HEIGHT = 390;

function ensureStyle() {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID))
    return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent =
    "@keyframes cpt-emr-left{from{opacity:0;transform:translateX(-14px)}to{opacity:1;transform:none}}" +
    "@keyframes cpt-emr-right{from{opacity:0;transform:translateX(14px)}to{opacity:1;transform:none}}" +
    "@keyframes cpt-emr-dot{0%,80%,100%{opacity:.25;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}" +
    ".cpt-emr-tile{flex:none;height:36px;min-width:36px;padding:0 6px;border-radius:10px;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 2px rgba(14,17,22,.08),0 6px 16px -10px rgba(14,17,22,.4)}" +
    ".cpt-emr-tile.l{animation:cpt-emr-left .38s cubic-bezier(.2,.8,.2,1) both}" +
    ".cpt-emr-tile.r{animation:cpt-emr-right .38s cubic-bezier(.2,.8,.2,1) both}" +
    ".cpt-emr-dots{display:flex;gap:4px}" +
    ".cpt-emr-dots span{width:5px;height:5px;border-radius:50%;background:#6bd888;animation:cpt-emr-dot 1.1s ease-in-out infinite both}" +
    ".cpt-emr-dots span:nth-child(2){animation-delay:.15s}.cpt-emr-dots span:nth-child(3){animation-delay:.3s}";
  document.head.appendChild(style);
}

const squash = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** The EMRS entry for the clinic's EMR ("Stride", "CORA", "webpt"…), or null. */
export function emrEntry(name) {
  const key = squash(name);
  if (!key) return null;
  return (
    EMRS.find((e) => squash(e.name) === key) ||
    EMRS.find(
      (e) => squash(e.name).startsWith(key) || key.startsWith(squash(e.name)),
    ) ||
    null
  );
}

export default function EmrConnecting({ emr }) {
  ensureStyle();
  const entry = emrEntry(emr);
  const name = entry?.name.replace(/2$/, "") || "your EMR";
  return (
    <div
      role="status"
      aria-live="polite"
      className="d-flex justify-content-center align-items-start"
      style={{
        height: NOTE_HEIGHT,
        paddingTop: 96,
        borderRadius: 12,
        background:
          "radial-gradient(120% 60% at 50% 22%, rgba(107,216,136,.12), transparent 70%), #f8faf9",
      }}
    >
      <div
        className="d-flex flex-column align-items-center"
        style={{
          gap: 10,
          padding: "16px 22px",
          maxWidth: "calc(100% - 24px)",
          borderRadius: 14,
          background: "#fff",
          boxShadow:
            "0 1px 2px rgba(14,17,22,.06), 0 14px 34px -16px rgba(14,17,22,.35)",
        }}
      >
        <span className="d-flex align-items-center" style={{ gap: 10 }}>
          <span className="cpt-emr-tile l">
            <img
              src={logo}
              alt="Comprehend"
              style={{
                width: 24,
                height: 24,
                objectFit: "contain",
                display: "block",
              }}
            />
          </span>
          <span className="cpt-emr-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="cpt-emr-tile r">
            {entry ? (
              <img
                src={entry.image}
                alt={name}
                style={{
                  height: 24,
                  maxWidth: 110,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <i
                className="fas fa-file-medical"
                style={{ color: "#8898aa" }}
                aria-hidden="true"
              />
            )}
          </span>
        </span>
        <span
          className="text-muted text-center"
          style={{ fontSize: 13, lineHeight: 1.3 }}
        >
          Reading the note from {name}…
        </span>
      </div>
    </div>
  );
}
